import { debounce } from "@material-ui/core";
import { useMemo, useRef, useEffect } from "react";

/**
 * A custom hook that wraps a handler function in a debounced function. Uses `useRef` to ensure the debounced function does not change between renders.
 *
 * @param handler A function to be debounced.
 * @param delay   The debounce delay.
 */
export const useDebouncedHandler = <T extends (...args: any[]) => any>(handler: T, delay: number) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  return useMemo(() => {
    const func = (...args: any[]) => {
      handlerRef.current(...args);
    };
    return debounce(func, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useAsyncDebouncedHandler = <T extends (...args: any[]) => Promise<any>>(
  handler: T,
  delay: number
) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  return useMemo(() => {
    const func = async (...args: any[]) => {
      await handlerRef.current(...args);
    };
    return debounce(func, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useDebouncedEffect = (effect: () => void, deps: unknown[], delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};
